import React from 'react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';
import { Heart, Users, Home, Baby, TrendingUp } from 'lucide-react';

const FamilyHarmony = () => {
  return (
    <div className="space-y-6">
      {/* Family Harmony Score */}
      <TaraCard variant="cosmic">
        <TaraCardHeader>
          <TaraCardTitle>Family Harmony Index</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Overall family dynamics and relationship strength
          </p>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="text-center mb-6">
            <div className="text-5xl font-bold text-cyan-600 mb-2">78%</div>
            <TaraBadge variant="calm" size="lg">Harmonious Phase</TaraBadge>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-medium text-stone-900">Home Peace</span>
              </div>
              <span className="text-sm font-bold text-cyan-600">85%</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-medium text-stone-900">Parent Relations</span>
              </div>
              <span className="text-sm font-bold text-cyan-600">72%</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-medium text-stone-900">Sibling Bond</span>
              </div>
              <span className="text-sm font-bold text-cyan-600">80%</span>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Childbirth Probability */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Baby className="w-5 h-5 text-pink-600" />
            Childbirth Windows
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-stone-900">2026-2027</p>
                  <p className="text-xs text-stone-600 mt-0.5">High probability window</p>
                </div>
                <TaraBadge variant="vibrant" size="sm">Favorable</TaraBadge>
              </div>
              <p className="text-xs text-stone-700 leading-relaxed">
                Jupiter's transit through 5th house creates optimal conditions. Moon dasha supports fertility.
              </p>
            </div>
            
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-stone-900">2029-2030</p>
                  <p className="text-xs text-stone-600 mt-0.5">Secondary window</p>
                </div>
                <TaraBadge variant="calm" size="sm">Good</TaraBadge>
              </div>
              <p className="text-xs text-stone-700 leading-relaxed">
                Venus dasha period with supportive planetary aspects.
              </p>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Family Growth Phases */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-600" />
            Family Growth Cycles
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5" />
              <div>
                <p className="font-medium text-stone-900">Current Phase: Strengthening</p>
                <p className="text-xs text-stone-600 mt-0.5">
                  4th house activation. Perfect time to resolve old conflicts and deepen bonds.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
              <div>
                <p className="font-medium text-stone-900">Next Phase: Expansion (2027)</p>
                <p className="text-xs text-stone-600 mt-0.5">
                  Family size may grow. New relationships or additions expected.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5" />
              <div>
                <p className="font-medium text-stone-900">Watch Phase (Oct 2025)</p>
                <p className="text-xs text-stone-600 mt-0.5">
                  Saturn aspect may create temporary tensions. Practice patience.
                </p>
              </div>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Recommendations */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle>Kutumba's Recommendations</TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-cyan-600 font-bold">•</span>
              <span className="text-stone-700">
                Schedule weekly family dinners on Thursdays (Jupiter day) for maximum harmony
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-600 font-bold">•</span>
              <span className="text-stone-700">
                Address lingering parent issues before February 2026 during reconciliation window
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-600 font-bold">•</span>
              <span className="text-stone-700">
                Consider home renovation or relocation between March-June 2026
              </span>
            </li>
          </ul>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default FamilyHarmony;
